import { Box, ButtonBase, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { fetchMe } from '../../actions/user.actions';
import { borderRadius, boxShadows, palette, spaces } from '../../assets/styles/themes/tokens';
import { selectUser } from '../../reducers/user.selectors';
import useAction from '../../utils/useAction';
import GradeCheckReveal from './GradeCheckReveal';
import { isPerfectGrade } from './grade';
import TrialReportCardDialog from './TrialReportCardDialog';
import { useGradeChangeReveal } from './useGradeChangeReveal';
import { useTrialReportCardEligibility } from './useTrialReportCardEligibility';
import { useTrialReportCardProgress } from './useTrialReportCardProgress';

interface TrialReportCardCardProps {
	isCollapsed: boolean;
}

// Neutral placeholder shown in the collapsed badge before the first area is
// completed (the expanded card uses the "Nicht bewertet" copy instead).
const NEUTRAL_BADGE_INDICATOR = '–';

// Centred wrapper for the collapsed badge only. No top padding — the card
// sits directly under the menu list.
const wrapperStyles = {
	width: '100%',
	padding: `0 ${spaces.xs} ${spaces.xs}`,
	display: 'flex',
	justifyContent: 'center',
};

// No top padding, side padding lines the card up with the sidebar sections.
const expandedWrapperStyles = {
	width: '100%',
	padding: `0 ${spaces.sm} ${spaces.xs}`,
	display: 'flex',
	justifyContent: 'flex-start',
};

// Compact one-row "paper" card: title on the left, grade section on the right.
// The border is neutral by default and turns blue while the dialog is open.
// Fixed height so the card doesn't grow when a grade replaces the
// "Not graded" one-liner.
const getExpandedCardStyles = (isActive: boolean) => ({
	width: '100%',
	height: '50px',
	borderRadius: borderRadius.xs,
	boxShadow: boxShadows.elevation1,
	backgroundColor: palette.common.white,
	border: `1.5px solid ${isActive ? palette.primary.outlinedBorder : palette.elevation.outlined}`,
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: `0 ${spaces.sm}`,
	transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
	'&:hover': {
		transform: 'translateY(-2px)',
		boxShadow: boxShadows.elevation2,
	},
});

const headlineWrapperStyles = {
	position: 'relative',
	paddingBottom: '3px',
};

const headlineStyles = {
	fontSize: '8px',
	fontWeight: 700,
	lineHeight: 1,
	letterSpacing: '0.09em',
	textTransform: 'uppercase',
	color: palette.text.primary,
};

const headlineUnderlineStyles = {
	position: 'absolute',
	left: '-1px',
	right: '-1px',
	bottom: 0,
	height: '2px',
	borderRadius: '2px',
	backgroundColor: palette.accent.pink,
	transform: 'rotate(-1.2deg)',
};

// Right-hand grade section: tiny label sitting above the grade value.
const gradeSectionStyles = {
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-end',
};

const subHeadlineStyles = {
	fontSize: '7px',
	lineHeight: 1.2,
	color: palette.text.secondary,
};

const gradeStyles = {
	fontSize: '20px',
	fontWeight: 700,
	lineHeight: 1.1,
	fontVariantNumeric: 'tabular-nums',
};

const ungradedStyles = {
	fontSize: '11px',
	fontStyle: 'italic',
	lineHeight: 1.2,
	color: palette.text.secondary,
};

const getBadgeStyles = (isActive: boolean) => ({
	width: '32px',
	height: '32px',
	borderRadius: borderRadius.xs,
	border: `1px solid ${isActive ? palette.primary.outlinedBorder : palette.elevation.outlined}`,
	backgroundColor: palette.common.white,
	boxShadow: boxShadows.elevation1,
});

const TrialReportCardCard: React.FC<TrialReportCardCardProps> = ({ isCollapsed }) => {
	const { t } = useTranslation();
	const user = useSelector(selectUser);
	const eligible = useTrialReportCardEligibility();
	const progress = useTrialReportCardProgress();
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [dialogOrigin, setDialogOrigin] = useState<{ x: number; y: number }>();
	const fetchMeAction = useAction(fetchMe);
	const { shouldAnimate: playGradeReveal, onRevealed } = useGradeChangeReveal(
		progress.grade.label,
		user?.id,
		eligible
	);

	if (!eligible) {
		return null;
	}

	const { grade } = progress;
	// Grade is blue (the in-app accent) until every area is done, then green.
	const gradeColor = isPerfectGrade(grade) ? palette.success.main : palette.primary.main;

	const openDialog = (event: React.MouseEvent<HTMLElement>) => {
		// Progress may have advanced in another tab — refetch /me so the dialog
		// opens with current grades (`true` forces past the in-Redux short-circuit;
		// silent, else loading gates swap the page for a loader and kill the dialog).
		fetchMeAction(true, { silent: true });
		// Keyboard activation synthesizes a click at (0,0) — anchor the dialog's
		// opening animation to the trigger's centre instead.
		if (event.clientX === 0 && event.clientY === 0) {
			const rect = event.currentTarget.getBoundingClientRect();
			setDialogOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
		} else {
			setDialogOrigin({ x: event.clientX, y: event.clientY });
		}
		setIsDialogOpen(true);
	};

	return (
		<>
			{isCollapsed ? (
				<Box data-testid="trial-report-card-container" sx={wrapperStyles}>
					<ButtonBase
						onClick={openDialog}
						data-testid="trial-report-card-badge"
						aria-label={t('Report Card')}
						sx={getBadgeStyles(isDialogOpen)}
					>
						<Typography
							variant="subtitle3"
							data-testid="trial-report-card-badge-grade"
							sx={{
								fontWeight: 700,
								fontVariantNumeric: 'tabular-nums',
								color: grade.label ? gradeColor : palette.text.secondary,
							}}
						>
							{grade.label ? (
								<GradeCheckReveal
									key={grade.label}
									label={grade.label}
									color={gradeColor}
									animate={playGradeReveal}
									onRevealed={onRevealed}
									variant="badge"
								/>
							) : (
								NEUTRAL_BADGE_INDICATOR
							)}
						</Typography>
					</ButtonBase>
				</Box>
			) : (
				<Box data-testid="trial-report-card-container" sx={expandedWrapperStyles}>
					<ButtonBase
						onClick={openDialog}
						data-testid="trial-report-card"
						sx={getExpandedCardStyles(isDialogOpen)}
					>
						<Box sx={headlineWrapperStyles}>
							<Typography data-testid="trial-report-card-title" sx={headlineStyles}>
								{t('Report Card')}
							</Typography>
							<Box sx={headlineUnderlineStyles} aria-hidden="true" />
						</Box>
						{grade.label ? (
							<Box sx={gradeSectionStyles}>
								<Typography data-testid="trial-report-card-grade-label" sx={subHeadlineStyles}>
									{t('Overall Grade')}
								</Typography>
								<Typography
									data-testid="trial-report-card-grade"
									sx={{ ...gradeStyles, color: gradeColor }}
								>
									{/* Keyed by value; the celebratory check draw plays only for the final 1,0. */}
									<GradeCheckReveal
										key={grade.label}
										label={grade.label}
										color={gradeColor}
										animate={playGradeReveal}
										onRevealed={onRevealed}
									/>
								</Typography>
							</Box>
						) : (
							<Typography data-testid="trial-report-card-ungraded" sx={ungradedStyles}>
								{t('Not graded')}
							</Typography>
						)}
					</ButtonBase>
				</Box>
			)}
			<TrialReportCardDialog
				open={isDialogOpen}
				onClose={() => setIsDialogOpen(false)}
				firstName={user?.firstName}
				progress={progress}
				openOrigin={dialogOrigin}
			/>
		</>
	);
};

export default TrialReportCardCard;
